import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Quote, ArrowUpRight } from "lucide-react";

function TeamCard({ m, index }) {
    return (
        <motion.article
            data-testid={`team-card-${m.slug}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1], delay: (index % 3) * 0.08 }}
            className="group border border-[rgba(255,255,240,0.12)] bg-[var(--navy-deep)] overflow-hidden flex flex-col"
        >
            <div className="relative aspect-[3/4] overflow-hidden bg-[var(--navy)]">
                <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3 label-mono bg-[rgba(6,17,36,0.7)] backdrop-blur-md px-3 py-1.5 border border-[rgba(201,162,75,0.4)] text-[var(--gold)]">
                    {m.category === "leadership" ? "LEADERSHIP" : "AMBASSADOR"}
                </div>
                {m.country && (
                    <div className="absolute bottom-3 left-3 label-mono flex items-center gap-2 bg-[rgba(6,17,36,0.7)] backdrop-blur-md px-3 py-1.5 border border-[rgba(255,255,240,0.2)]">
                        <MapPin className="w-3 h-3 text-[var(--gold)]" /> {m.country}
                    </div>
                )}
            </div>
            <div className="p-6 flex flex-col flex-1">
                <h3 className="serif-display text-2xl md:text-3xl leading-tight">{m.name}</h3>
                <div className="label-mono text-[var(--gold)] mt-2">{m.role}</div>
                {m.quote && (
                    <p className="mt-4 serif-italic text-[var(--ivory)]/80 text-lg leading-snug flex gap-2">
                        <Quote className="w-4 h-4 shrink-0 mt-1 text-[var(--gold)]" /> {m.quote}
                    </p>
                )}
                {m.blurb && m.blurb.split("\n\n").map((para, i) => (
                    <p key={i} className={`text-sm text-[var(--ivory)]/60 leading-relaxed ${i === 0 ? "mt-4" : "mt-3"}`}>{para}</p>
                ))}
            </div>
        </motion.article>
    );
}

export default function TeamSection() {
    const [team, setTeam] = useState([]);

    useEffect(() => {
        api.get("/team").then(({ data }) => setTeam(data.team || [])).catch(() => setTeam([]));
    }, []);

    if (!team.length) return null;

    const leadership = team.filter((m) => m.category === "leadership");
    const ambassadors = team.filter((m) => m.category === "ambassador");

    return (
        <section data-testid="team-section" className="py-24 md:py-40 bg-[var(--navy)]">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-24">
                    <div className="md:col-span-4">
                        <div className="label-mono">— THE PEOPLE</div>
                    </div>
                    <div className="md:col-span-8">
                        <h2 className="serif-display text-4xl md:text-6xl lg:text-7xl">
                            Leadership &amp; <span className="serif-italic text-[var(--gold)]">ambassadors.</span>
                        </h2>
                        <p className="mt-6 text-lg text-[var(--ivory)]/70 max-w-2xl">
                            A global team turning the mission into motion — from the executive office to founding ambassadors carrying ZSGI into their countries.
                        </p>
                    </div>
                </div>

                {leadership.length > 0 && (
                    <>
                        <div className="label-mono text-[var(--ivory)]/50 mb-6">— EXECUTIVE LEADERSHIP</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
                            {leadership.map((m, i) => <TeamCard key={m.slug} m={m} index={i} />)}
                        </div>
                    </>
                )}

                {ambassadors.length > 0 && (
                    <>
                        <div className="label-mono text-[var(--ivory)]/50 mb-6">— FOUNDING COUNTRY AMBASSADORS</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {ambassadors.map((m, i) => <TeamCard key={m.slug} m={m} index={i} />)}
                        </div>
                    </>
                )}

                {/* Become a Country Ambassador */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                    className="mt-16 md:mt-24 border border-[var(--gold)] p-8 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8"
                    data-testid="become-ambassador-cta"
                >
                    <div className="max-w-2xl">
                        <div className="label-mono text-[var(--gold)] mb-4">— YOUR COUNTRY NEXT?</div>
                        <h3 className="serif-display text-3xl md:text-5xl leading-[1.02]">
                            Become a <span className="serif-italic text-[var(--gold)]">Founding Country Ambassador.</span>
                        </h3>
                        <p className="mt-5 text-[var(--ivory)]/75 text-lg">
                            We're onboarding changemakers country by country. Represent ZSGI where you are — build partnerships, empower youth, and put your nation on the map.
                        </p>
                    </div>
                    <Link
                        to="/contact?interest=ambassador"
                        className="btn-gold shrink-0 self-start md:self-center"
                        data-testid="become-ambassador-btn"
                    >
                        Apply to represent your country <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
